<template>
  <div>
    <div>
      <div :class="{'my-certificate': !isSingleSession || (isSingleSession && buttons?.evaluation?.status)}">
        <div
          v-if="!isFetching && !isSingleSession || (isSingleSession && buttons?.evaluation?.status)"
          class="my-certificate__summary"
        >
          <event-summary />
        </div>

        <div class="agenda-wrapper">
          <div class="agenda-notify">
            <!-- Notification Message -->
            <Notify
              v-if="notification?.enabled"
              :theme="notification?.type"
              v-html="notification?.text"
            ></Notify>
          </div>

          <welcome-message />

          <additional-fixed-tabs
            v-if="!isSingleSession"
            :tabs="CEInfoTabs"
            :show-filter-button="false"
          />

          <div class="agenda-page">

            <!--       INDICATOR     -->
            <div v-if="isFetching" class="page-loader">
              <c-loader />
            </div>

            <!--      Empty list      -->
            <div v-else-if="!isFetching && !visibleSessions?.length && !errorStatus">
              <c-list-empty
                style="margin-top: 50px;"
                title="The list is empty"
                description="Sessions attended will appear on this page and be included on your Certificate."
              ></c-list-empty>
            </div>

            <!--       ERROR -->
            <div v-else-if="!isFetching && !visibleSessions?.length && errorStatus">
              <ErrorPage :status="errorStatus"/>
            </div>

            <!--       Data     -->
            <div v-else class="agenda-group">
              <div v-if="visibleSessions?.length" class="agenda-day">
                <div>
                  <div
                    v-for="(session, index) in visibleSessions"
                    :key="session.id"
                    class="agenda-list"
                  >
                    <div v-if="shouldShowDay(index)" class="agenda-list-day">
                      <h3>{{ formatDate(session.start_at) }}</h3>
                    </div>

                    <agenda-item
                      current-page="My Certificate"
                      :hidden-schedule-button="true"
                      :show-ce-hours-on-mobile="true"
                      :session="session"
                      :index="index"
                      :allow-add-to-scheduler="attendee?.tabs?.my_agenda"
                      :group-time-frame="
                        visibleSessions[index - 1] &&
                        visibleSessions[index - 1].start_at_formatted ===
                        visibleSessions[index].start_at_formatted &&
                        visibleSessions[index - 1].end_at_formatted ===
                        visibleSessions[index].end_at_formatted
                      "
                    />
                  </div>

                  <div
                    v-if="visibleSessionsCount < totalCertificateSessions"
                    class="load-more-trigger"
                    v-observe-visibility="handleVisibilityChange"
                  >
                    <c-button
                      theme="default"
                      size="sm"
                      icon="fa-solid fa-arrow-down"
                      :show-indicator="true"
                      fill
                      uppercase
                      @click="loadMore"
                    >Load More ...</c-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import {ROUTES} from "../../../router";
import AgendaItem from "../common/agenda-item";
import AdditionalFixedTabs from "../../../components/ui/AdditionalFixedTabs";
import Notify from "../../../components/ui/Notify";
import DownloadCertificateFormModal from "../../../components/agenda/download-certificate-form-modal";
import Container from "../../../components/ui/Container";
import EventSummary from "../../../components/agenda/event-summary";
import WelcomeMessage from "../common/welcome-message";
import common from "../../../helpers/common";
import ErrorPage from "../../error-page";

export default {
  components: {
    WelcomeMessage,
    AdditionalFixedTabs,
    Notify,
    AgendaItem,
    DownloadCertificateFormModal,
    Container,
    EventSummary,
    ErrorPage,
  },
  beforeMount() {
    this.$store.commit("agendaStore/updateIsLoadedSessionDetails", false);
  },
  async mounted() {
    common.setHeaderText('Event');

    await this.$store.dispatch("agendaStore/fetchAgenda", {
      attendeeId: this.attendeeId,
      conferenceId: this.attendee?.conference_id
    });
  },
  data() {
    return {
      attendeeId: this.$route.params.attendeeId,
      eventEvaluationPath: ROUTES.AGENDA_EVALUATION.replace(':attendeeId', this.$route.params.attendeeId),
      eventQuizPath: ROUTES.AGENDA_QUIZ.replace(':attendeeId', this.$route.params.attendeeId),
    }
  },
  computed: {
    ...mapGetters('attendeeStore', [
      'attendee',
      'CEInfoTabs',
      'isSingleSession',
      'notification'
    ]),

    ...mapGetters('agendaStore', [
      'isFetching',
      'buttons',
      'flatSessions',
      'visibleSessionsCount',
      'defaultSessionsCount',
      'errorStatus'
    ]),

    visibleSessions() {
      return this.flatSessions
          .filter(session => session.my_certificate === true)
          .slice(0, this.visibleSessionsCount);
    },

    totalCertificateSessions() {
      return this.flatSessions.filter(session => session.my_certificate === true).length;
    },
  },
  methods: {
    formatDate(dateStr) {
      const options = {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'};
      return new Date(dateStr).toLocaleDateString('en-US', options);
    },

    shouldShowDay(index) {
      if (index === 0) return true;

      const current = this.visibleSessions[index];
      const previous = this.visibleSessions[index - 1];

      return current.start_at.slice(0, 10) !== previous.start_at.slice(0, 10);
    },

    handleVisibilityChange(isVisible) {
      if (isVisible) {
        this.loadMore();
      }
    },

    loadMore() {
      const totalSessions = this.totalCertificateSessions;
      const visibleSessionsCount = Math.min(this.visibleSessionsCount + this.defaultSessionsCount, totalSessions);

      this.$store.commit("agendaStore/updateVisibleSessionsCount", visibleSessionsCount);
    },
  },
}
</script>

<style scoped lang="scss">
.my-certificate {
  display: grid;
  grid-template-columns: 1fr 400px;
  grid-gap: var(--attendee-gap-lg);
  max-width: 100%;
  width: 100%;

  @media (max-width: 1300px) {
    display: block;
  }

  &__summary {
    padding: var(--attendee-gap-lg);
    box-shadow: var(--attendee-box-shadow);
    background-color: var(--attendee-card-color);
    border-radius: var(--attendee-card-border-radius);
    position: sticky;
    top: 115px;
    height: calc(100vh - var(--attendee-header-height) - 60px);
    overflow: auto;
    order: 3;
    z-index: 22;

    @media (max-width: 1300px) {
      position: initial;
      order: 0;
      height: initial;
     margin-bottom: var(--attendee-gap-md);
    }

    @media (max-width: 900px) {
      margin: var(--attendee-gap-md);
    }
  }
}

.agenda-page-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100%;
}

.agenda-wrapper {
  background-color: white;
  border-radius: var(--attendee-card-border-radius);
  padding: var(--attendee-gap-md);

  @media (max-width: 900px) {
    padding: 0;
  }
}

// TODO DUPLICATE ON EVENT AGENDA AND SCHEDULER
.agenda-page {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: var(--attendee-gap-lg);
  max-width: 100%;
  width: 100%;
  margin-top: var(--attendee-gap-md);

  @media (max-width: 900px) {
    margin-top: 0;
  }

  .agenda-group {
    @media (max-width: 900px) {
      order: 2;
    }
  }

  h3 {
    color: var(--attendee-text-primary-color);
    font-size: 21px;
    margin-bottom: var(--attendee-gap-md);

    @media (max-width: 900px) {
      font-size: 16px;
      padding: var(--attendee-gap-md);
      margin-bottom: 0;
      text-align: center;
    }
  }
}

.agenda-day {
  margin-bottom: 50px;

  @media (max-width: 900px) {
    margin-bottom: 20px;
  }
}

.agenda-list {
  .agenda-list-day {
    @media (max-width: 900px) {
      padding: 0 var(--attendee-gap-md);
    }
  }
}
</style>
